import React, { useEffect } from 'react';
import countries from '../../../data/sidebar/countries.json';
import { nospace } from '~/utils/helpers';
import { type FieldValues, type RegisterOptions } from 'react-hook-form';
import { FieldProps, InputProps } from '../../../types/form';

const DEFAULT_MAX_LENGTH = 500;

export const DynamicFormField = (props: InputProps) => {
  switch (props.field.type) {
    case 'text':
      return <Text {...props} />;
    case 'textarea':
      return <Textarea {...props} />;
    case 'select':
      return <Select {...props} />;
    case 'checkbox':
      return <Checkbox {...props} />;
    case 'url':
      return <Text {...props} />;
    case 'email':
      return <Email {...props} />;
    case 'tel':
      return <Telephone {...props} />;
    case 'file':
      return <File {...props} />;
    default:
      return <TODO {...props} />;
  }
};

export const Text = ({ field, register, columnSpan, formId }: InputProps) => {
  const fieldId = nospace(`${formId}-${field.name}`);
  const spanClass = `col-span-${columnSpan ?? 1}`;

  return (
    <div className={`mb-[10px] grid ${spanClass} sm:col-span-full`}>
      <div className='flex items-baseline justify-between'>
        <Label className='text-md font-normal leading-loose text-text-primary'>
          {field.label}
        </Label>
      </div>
      <div>
        <input
          {...register(field.field_key, validate(field))}
          className={`selection:bg-text-placeholder box-border inline-flex w-full appearance-none items-center justify-center bg-[#fff] px-6 py-[1.75rem] text-[1.6rem] leading-normal text-text-primary outline-none border-rounded-strong placeholder:text-placeholder-text hover:shadow-[0_0_0_1px_theme(colors.blue-border)] focus:shadow-[0_0_0_2px_theme(colors.blue-border)]`}
          id={fieldId}
          type={field.type}
          placeholder={field.placeholder}
        />
      </div>
    </div>
  );
};

export const Textarea = ({
  field,
  register,
  columnSpan,
  formId,
}: InputProps) => {
  const fieldId = nospace(`${field.name}`);
  const spanClass = `col-span-${columnSpan ?? 1}`;

  return (
    <div className={`mb-[10px] grid ${spanClass} sm:col-span-full`}>
      <div className='flex items-baseline justify-between'>
        <Label className='text-md font-normal leading-loose text-text-primary'>
          {field.label}
        </Label>
      </div>
      <div>
        <textarea
          {...register(field.field_key, validate(field))}
          className='selection:bg-text-placeholder box-border inline-flex min-h-24 w-full appearance-none items-center justify-center bg-[#fff] px-6 py-[1.75rem] text-[1.6rem] leading-normal text-text-primary outline-none border-rounded-strong placeholder:text-placeholder-text hover:shadow-[0_0_0_1px_theme(colors.blue-border)] focus:shadow-[0_0_0_2px_theme(colors.blue-border)]'
          id={fieldId}
          placeholder={field.placeholder}
        />
      </div>
    </div>
  );
};

export const Email = ({ field, register, columnSpan, formId }: InputProps) => {
  const fieldId = nospace(`${formId}-${field.name}`);
  const spanClass = `col-span-${columnSpan ?? 1}`;

  return (
    <div className={`mb-[10px] grid ${spanClass} sm:col-span-full`}>
      <div className='flex items-baseline justify-between'>
        <Label className='text-md font-normal leading-loose text-text-primary'>
          {field.label}
        </Label>
      </div>
      <div>
        <input
          {...register(field.field_key, validate(field))}
          className='selection:bg-text-placeholder box-border inline-flex w-full appearance-none items-center justify-center bg-[#fff] px-6 py-[1.75rem] text-[1.6rem] leading-normal text-text-primary outline-none border-rounded-strong placeholder:text-placeholder-text hover:shadow-[0_0_0_1px_theme(colors.blue-border)] focus:shadow-[0_0_0_2px_theme(colors.blue-border)]'
          id={fieldId}
          type='email'
          placeholder={field.placeholder}
        />
      </div>
    </div>
  );
};

const Label = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return <div className={className}>{children}</div>;
};

export const Telephone = ({
  field,
  register,
  showError,
  columnSpan,
  formId,
}: InputProps) => {
  const fieldId = nospace(`${field.name}`);
  const spanClass = `col-span-${columnSpan ?? 1}`;
  const [selectedCountry, setSelectedCountry] = React.useState(
    countries.list.find(({ sortname }) => sortname === 'US') ??
      countries.list[0]
  );
  const modalRef = React.useRef<HTMLDivElement>(null);
  const [opened, setOpened] = React.useState(false);

  const handlePhoneCode = (countrySortname: any) => () => {
    const country = countries.list.find(
      ({ sortname }) => sortname === countrySortname
    );
    if (country) {
      setSelectedCountry(country);
      setOpened(!opened);
    }
  };

  const handleMenuState = () => {
    setOpened(!opened);
  };

  const closeMenu = (event: React.FocusEvent<HTMLButtonElement>) => {
    const dataCountry = (event.relatedTarget as HTMLElement)?.getAttribute(
      'data-country'
    );
    if (dataCountry) {
      return;
    }
    setOpened(false);
  };

  const handleFocus = () => {
    if (modalRef.current?.focus) {
      modalRef.current.focus();
    }
  };

  return (
    <div className={`mb-[10px] grid ${spanClass} sm:col-span-full`}>
      <div className='flex items-baseline justify-between'>
        <Label className='text-md font-normal leading-loose text-text-primary'>
          {field.label}
        </Label>
      </div>
      <div className='relative flex'>
        <div className='z-10'>
          <button
            type='button'
            className='flex h-full items-center gap-4 rounded-l-[20px] bg-text-primary px-6 py-[1.75rem] leading-normal hover:shadow-[0_0_0_1px_theme(colors.blue-border)] focus:shadow-[0_0_0_2px_theme(colors.blue-border)]'
            onClick={handleMenuState}
            onBlur={closeMenu}
          >
            <img
              className='flag h-full max-h-[2rem] w-[3rem]'
              src={`/flags/${selectedCountry?.sortname}.svg`}
            />
            <i
              className={`w-6 text-xs h-6 select-arrow ${opened ? 'rotate-180' : ''}`}
            />
          </button>
          <div
            className={`hidden data-[state=opened]:block`}
            data-state={opened ? 'opened' : 'closed'}
            onMouseOver={handleFocus}
            ref={modalRef}
          >
            <div className='absolute left-0 top-16 h-[20rem] w-full overflow-scroll bg-[#fff] p-2 shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)]'>
              {countries.list.map(country => (
                <button
                  type='button'
                  key={country.sortname}
                  className='flex w-full items-center justify-start gap-2 bg-[#fff] px-6 py-4 text-text-primary hover:bg-placeholder-text hover:text-[#fff]'
                  onClick={handlePhoneCode(country.sortname)}
                  data-country={country.sortname}
                >
                  <div className='flag h-auto w-[2rem]'>
                    <img
                      src={`/flags/${country.sortname}.svg`}
                      loading='lazy'
                    />
                  </div>
                  <div className='overflow-hidden text-ellipsis text-left'>
                    {`${country.name} (+${country.phone_code})`}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className='flex-1'>
          <input
            {...register(field.field_key, validate(field))}
            className='selection:bg-text-placeholder box-border inline-flex w-full appearance-none items-center justify-center rounded-r-[20px] bg-[#fff] px-6 py-[1.75rem] text-[1.6rem] leading-normal text-text-primary outline-none placeholder:text-placeholder-text hover:shadow-[0_0_0_1px_theme(colors.blue-border)] focus:shadow-[0_0_0_2px_theme(colors.blue-border)]'
            type={'tel'}
            id={fieldId}
            placeholder={field.placeholder}
          />
        </div>
      </div>
    </div>
  );
};

export const Select = ({
  field,
  register,
  setValue,
  columnSpan,
}: InputProps) => {
  const [selected, setSelected] = React.useState('');
  const [opened, setOpened] = React.useState(false);
  const spanClass = `col-span-${columnSpan ?? 1}`;
  const modalRef = React.useRef<HTMLDivElement>(null);

  const handleFocus = () => {
    if (modalRef.current?.focus) {
      modalRef.current.focus();
    }
  };

  const handleMenuState = () => {
    setOpened(!opened);
  };

  const closeMenu = (event: React.FocusEvent<HTMLButtonElement>) => {
    const selectionState = (event.relatedTarget as HTMLElement)?.getAttribute(
      'data-selection'
    );
    if (selectionState) {
      return;
    }
    setOpened(false);
  };

  const handleSelection = (event: React.MouseEvent<HTMLButtonElement>) => {
    const { innerText } = event.currentTarget;
    setValue(field.field_key, innerText);
    setSelected(innerText);
    setOpened(false);
  };

  useEffect(() => {
    register(field.field_key, validate(field));
  }, [register]);

  return (
    <div className={`mb-[10px] grid ${spanClass} sm:col-span-full`}>
      <div className='flex items-baseline justify-between'>
        <Label className='text-md font-normal leading-loose text-text-primary'>
          {field.label}
        </Label>
      </div>
      <div className='relative bg-[#fff] border-rounded-strong hover:shadow-[0_0_0_1px_theme(colors.blue-border)] focus:shadow-[0_0_0_2px_theme(colors.blue-border)]'>
        <button
          type='button'
          className='box-border flex w-full items-center gap-4 pr-6 text-text-primary border-rounded-strong before:content-normal before:bg-text-primary before:bg-select-arrow before:bg-center before:bg-no-repeat before:pb-[5.9rem] before:pl-[5.9rem] before:border-rounded-strong'
          onClick={handleMenuState}
          onBlur={closeMenu}
        >
          {/* <i
            className={`select-arrow aspect-square h-full bg-text-primary leading-normal text-[#fff] border-rounded-strong`}
          /> */}
          {selected.length === 0 && (
            <span className='overflow-hidden text-ellipsis py-[1.75rem] text-[1.6rem] leading-normal text-placeholder-text'>
              {field.placeholder}
            </span>
          )}
          {selected.length > 0 && (
            <span className='overflow-hidden text-ellipsis py-[1.75rem] text-[1.6rem] leading-normal text-text-primary'>
              {selected}
            </span>
          )}
        </button>
        <div
          className={`absolute left-0 top-16 z-10 hidden w-full data-[state=opened]:block`}
          data-state={opened ? 'opened' : 'closed'}
          onMouseOver={handleFocus}
          ref={modalRef}
        >
          <div className='overflow-hidden rounded-md bg-[#fff] shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)]'>
            <div className='p-[5px]'>
              {field.options?.map(option => (
                <button
                  key={option.value}
                  type='button'
                  className='flex w-full items-center gap-2 bg-[#fff] px-6 py-4 text-text-primary hover:bg-placeholder-text hover:text-[#fff]'
                  onClick={handleSelection}
                  data-selection={option.name}
                >
                  {option.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Checkbox = ({
  field,
  setValue,
  showError,
  register,
  formId,
  columnSpan,
}: InputProps) => {
  const spanClass = `col-span-${columnSpan ?? 1}`;
  const [checkedItems, setCheckedItems] = React.useState<string[]>([]);
  const [hasItems, setHasItems] = React.useState(false);
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    let checkedCopy = [...checkedItems];
    if (checkedItems.includes(value)) {
      checkedCopy = checkedItems.filter(item => item !== value);
    } else {
      checkedCopy.push(value);
      setHasItems(true);
    }
    setValue(field.field_key, checkedCopy);
    setCheckedItems(checkedCopy);
  };

  useEffect(() => {
    register(field.field_key, {
      validate: (value: any[]) => {
        const checkedCount = value.filter(Boolean).length;
        if (checkedCount > 0) {
          setHasItems(true);
          return true;
        }
        setHasItems(false);
        return `Please select at least one of the ${field.name.toLowerCase()}`;
      },
    });
  }, [register]);

  return (
    <div className={`mb-[10px] grid ${spanClass} sm:col-span-full`}>
      <div className='mt-2 flex flex-col items-baseline justify-between'>
        <Label className='text-md font-normal leading-loose text-text-primary'>
          {field.label}
        </Label>
        <div className='grid w-full grid-cols-3 gap-4 md:grid-cols-2'>
          {field.options?.map((option, index) => {
            const fieldId = nospace(`${formId}-${field.name}-${option.value}`);
            const isChecked = checkedItems.includes(option.value);
            return (
              <label
                key={fieldId}
                htmlFor={fieldId}
                className='ease relative flex items-stretch justify-stretch bg-[#fff] text-[1.6rem] leading-normal text-text-primary outline-none transition-colors border-rounded-strong hover:bg-text-primary hover:text-[#fff] hover:shadow-[0_0_0_1px_theme(colors.blue-border)] focus:shadow-[0_0_0_2px_theme(colors.blue-border)] data-[state=checked]:bg-text-primary data-[state=checked]:text-[#fff] data-[has=error]:shadow-[0_0_0_2px_theme(colors.blue-border)]'
                data-state={isChecked ? 'checked' : 'unchecked'}
                data-has={!hasItems && showError ? 'error' : 'items'}
              >
                <input
                  {...register(`${field.field_key}.${index}`)}
                  id={fieldId}
                  type='checkbox'
                  className={`hidden`}
                  checked={isChecked}
                  onChange={handleCheckboxChange}
                  value={option.value}
                />
                <span className='overflow-hidden text-ellipsis p-[1.75rem]'>
                  {option.name}
                </span>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const File = ({ field, register, formId, columnSpan }: InputProps) => {
  const fieldId = nospace(`${formId}-${field.name}`);
  const spanClass = `col-span-${columnSpan ?? 1}`;

  return (
    <div className={`mb-[10px] grid ${spanClass} sm:col-span-full`}>
      <div className='flex items-baseline justify-between'>
        <Label className='text-md font-normal leading-loose text-text-primary'>
          {field.label}
        </Label>
      </div>
      <div>
        <input
          {...register(field.field_key, validate(field))}
          type='file'
          id={fieldId}
          className='selection:bg-text-placeholder file:text-xs box-border inline-flex w-full appearance-none items-center justify-center bg-[#fff] px-6 py-[1.675rem] text-[1.6rem] leading-normal text-text-primary outline-none border-rounded-strong file:mr-4 file:rounded-2xl file:border-0 file:bg-placeholder-text file:px-4 file:py-1 file:font-semibold file:text-[#fff] placeholder:text-placeholder-text hover:shadow-[0_0_0_1px_theme(colors.blue-border)] focus:shadow-[0_0_0_2px_theme(colors.blue-border)]'
          placeholder={field.placeholder}
          name={field.field_key}
          accept='image/png,image/jpeg,image/webp'
        />
      </div>
    </div>
  );
};

/**
 * Placeholder for unimplemented fields
 */
const TODO = (props: InputProps) => {
  return (
    <div className='text-text-primary'>{`TODO ${props.field.name} - ${props.field.type}`}</div>
  );
};

/**
 * Submit Button
 */
export const Submit = ({
  label,
  pending,
  submitted,
}: {
  label: string;
  pending: boolean;
  submitted: boolean;
}) => {
  return (
    <div>
      <button
        type='submit'
        name='submit'
        className='bunny-button bunny-button--primary flex w-fit items-center gap-4 px-16 py-[1.8rem] font-medium text-[#fff] transition-[width] duration-300 ease-in border-rounded-strong'
        disabled={pending || submitted}
      >
        {pending ? <i className='fas fa-spinner fa-spin' /> : null}
        {label}
      </button>
    </div>
  );
};

/**
 * Terms and Conditions Checkbox
 */
export const TermsAndConditions = ({
  formId,
  register,
  color = 'blue',
}: {
  formId: string;
  register?: any;
  color?: 'orange' | 'blue';
}) => {
  const tocId = `${formId}-terms`;
  const [checked, setChecked] = React.useState(false);
  const style = {
    orange: {
      link: 'text-orange-accent hover:text-orange-accent focus:text-orange-accent',
      checkbox:
        'bg-[#fff] data-[state=checked]:bg-orange-accent text-[#fff] focus:shadow-[0_0_0_2px_theme(colors.blue-border)] hover:shadow-[0_0_0_1px_theme(colors.blue-border)]',
    },
    blue: {
      link: 'text-text-primary hover:text-text-primary active:text-text-primary',
      checkbox:
        'bg-[#fff] data-[state=checked]:bg-placeholder-text text-[#fff] focus:shadow-[0_0_0_2px_theme(colors.blue-border)] hover:shadow-[0_0_0_1px_theme(colors.blue-border)]',
    },
  };

  function handleTermsChange() {
    setChecked(!checked);
  }

  return (
    <div className={`mt-8 flex items-center gap-2`}>
      <div className='w-8 relative flex h-8 items-center justify-center'>
        <input
          {...register('terms_and_conditions', {
            required: {
              value: true,
              message: 'You must agree to the bunny.net Privacy Policy.',
            },
          })}
          id={tocId}
          type='checkbox'
          className={`shadow-0_0_1px_black absolute left-0 top-0 h-full w-full appearance-none items-center justify-center rounded-[4px] ${style[color].checkbox} shadow-[0_2px_10px] outline-none`}
          onChange={handleTermsChange}
          name='terms_and_conditions'
          checked={checked}
          data-state={checked ? 'checked' : 'unchecked'}
          aria-label='I have read and agree to the bunny.net Privacy Policy.'
        ></input>

        <div
          className={`pointer-events-none z-[1] touch-none leading-tight`}
          aria-hidden={true}
        >
          <i
            className='fas fa-check text-xs'
            aria-hidden={true}
          />
        </div>
      </div>
      <label
        htmlFor={tocId}
        className={`text-xs text-text-primary`}
      >
        I have read and agree to the bunny.net{' '}
        <a
          className={`font-semibold ${style[color].link} hover:underline`}
          href='/privacy'
        >
          Privacy Policy.
        </a>
      </label>
    </div>
  );
};

/**
 * Error Messages validation
 */
const validate = (field: FieldProps): RegisterOptions<FieldValues> => {
  return {
    required: {
      value: field.required,
      message: `${field.label} is required`,
    },
    maxLength: {
      value: field.maxLength ?? DEFAULT_MAX_LENGTH,
      message: `${field.label} is too long`,
    },
    pattern: {
      value: field.pattern ? new RegExp(field.pattern) : new RegExp('.*'),
      message: `${field.label} is invalid`,
    },
  };
};
